import { HTMLProps, JSX } from "react";

import { Icon } from "components/icon/Icon";

import "./FolderTree.scss";

export type TreeNode = {
  id: string;
  parentId?: string;
};

export type TreeFolder = TreeNode & {
  name: string;
  header: boolean;
  open: boolean;
  writable: boolean;
  empty: boolean;
  sortOrder?: number;
};

export type FolderTreeProps<T extends TreeFolder> =
  HTMLProps<HTMLUListElement> & {
    folders: T[];
    selectedFolderId: string;
    renderFolderNode?: (node: T, level: number) => JSX.Element;
    onFolderClick?: (node: T, hasChildren: boolean) => void;
    rootFolderId?: string;
    classNamesFolder?: string;
  };

/**
 * Get the path of nodes that lead up to the given child node.
 *
 * With the following tree, the path to "target" is ["root", "2", "22"]
 *
 * root
 *  |- 1
 *  |- 2
 *     |
 *     |- 21
 *     |
 *     |- 22
 *        |- target
 *
 * @param treeNodes
 * @param startChild
 * @returns
 */
export function getNodePath(
  treeNodes: TreeNode[],
  startChild: TreeNode
): TreeNode[] {
  function findPath(child: TreeNode): TreeNode[] {
    const parent = treeNodes.find((n) => child.parentId === n.id);

    if (!parent) {
      return [];
    }

    return [parent, ...findPath(parent).flat()];
  }

  return findPath(startChild);
}

export default function FolderTree<T extends TreeFolder>({
  folders,
  selectedFolderId,
  renderFolderNode = (node, _) => <>{node.name}</>,
  className = "",
  onFolderClick = () => {},
  rootFolderId = "root",
  classNamesFolder = "py-3",
  ...rest
}: FolderTreeProps<T>) {
  const current = folders.find((f) => f.id === selectedFolderId);
  const openNodes: TreeNode[] = !current ? [] : getNodePath(folders, current);

  const sortByOrderAndName = (a: T, b: T) => {
    if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
      return a.sortOrder - b.sortOrder;
    }

    return a.name < b.name ? -1 : 1;
  };

  const renderFolderTree = (folder: T, folderLevel = 1) => {
    const childNodes = folders.filter((n: T) => n.parentId === folder.id);
    const selected = selectedFolderId === folder.id;
    const isOpen = openNodes.map((n) => n.id).includes(folder.id) || selected;

    const classes = [
      "d-flex justify-content-between align-items-center",
      selected && "selected",
      isOpen && "open",
      folder.header ? "fileHeader" : "fileFolder",
      folder.id === rootFolderId && "root",
      folder.writable && "writable",
      folder.empty && "empty",
      classNamesFolder
    ];

    const paddingLeft = `${10 * folderLevel}px`;
    const hasChildren = childNodes.length > 0;

    return (
      <li key={folder.id}>
        <div
          className={classes.filter((c) => c).join(" ")}
          onClick={() => !folder.header && onFolderClick(folder, hasChildren)}
        >
          <div style={{ paddingLeft }} className="d-flex align-items-center">
            {renderFolderNode(folder, folderLevel)}

            {hasChildren && !isOpen && !folder.header && (
              <Icon
                style={{ fontSize: "10px" }}
                className="pl-3"
                name="chevron-right"
              />
            )}

            {hasChildren && isOpen && !folder.header && (
              <Icon
                style={{ fontSize: "10px" }}
                className="pl-3"
                name="chevron-down"
              />
            )}
          </div>
        </div>
        <ul>
          {(isOpen || folder.header) &&
            childNodes
              .sort(sortByOrderAndName)
              .map((c) => renderFolderTree(c, folderLevel + 1))}
        </ul>
      </li>
    );
  };

  if (!folders.length) {
    return <>Det finns inga mappar!</>;
  }

  const root = folders.find((n) => n.id === rootFolderId);
  if (!root) {
    return <>Det finns ingen rotmapp!</>;
  }

  const rootFolders = folders.filter((n) => n.parentId === rootFolderId);

  if (!rootFolders) {
    return <>Det finns inga mappar i roten!</>;
  }

  return (
    <ul className={`file-tree ${className}`} {...rest}>
      {rootFolders.map((folder) => renderFolderTree(folder, 0))}
    </ul>
  );
}
